import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import ShopifyIntegrationCard from "@/components/brand/settings/integrations/shopify-integration-card"
import TiktokIntegrationCard from "@/components/brand/settings/integrations/tiktok-integration-card"
import HeadingSmall from "@/components/heading-small"
import { useBrandSyncLive } from "@/hooks/use-brand-sync-live"
import AppLayout from "@/layouts/app-layout"
import type { BrandSettingsIntegrationsProps } from "@/types/integrations"

/**
 * OKS-462 — Brand Settings → Integrations page.
 *
 * The brand's own TikTok account connection (connect / status /
 * disconnect). Built to hold future integrations (one card per
 * provider), but only TikTok ships now.
 *
 * State + data flow is server-driven: the `tiktok.connection` prop is
 * the source of truth for the card's five states (not-connected /
 * connected / expired / error + non-admin read-only). Connect/disconnect
 * are server round-trips; a successful connect returns via the shared
 * OAuth callback with a flash toast, disconnect via the controller flash.
 *
 * `can_manage` (server `connect?`) gates the whole action row — members
 * see status read-only with NO action affordance (DOM-absent).
 *
 * **No shadcn `<Form>`** — the disconnect uses Inertia `<Form>`; connect
 * uses the native `<TiktokOAuthStartForm>` (cross-origin OAuth handoff).
 * Flash → toast via `useFlash` (mounted in the persistent layout).
 */
export default function BrandSettingsIntegrations({
  tiktok,
  shopify,
  can_manage,
}: BrandSettingsIntegrationsProps) {
  const { t } = useTranslation()

  // OKS-960 — S2's status line goes live while an import the viewer started is
  // running. Reloads only the `shopify` prop group: the TikTok card is
  // independent and has nothing to recompute.
  //
  // ⚠️ DISABLED while there is no store, and that is Core Rule 12, not an
  // optimisation. An import can OUTLIVE the store it was importing: a brand
  // disconnects mid-import, lands back on the setup wizard, starts typing a
  // corrected address — and a signal from the still-running job arrives and
  // reloads `shopify`, which now resolves to no store. The job cannot know its
  // store was destroyed underneath it.
  //
  // With no store there is nothing live to show here anyway (the ticker lives
  // on the connected panel), so suppressing the subscription removes the
  // hazard rather than guarding it. The symptom it prevents — a wizard that
  // appears to reset itself for no visible reason — is the worst kind of bug
  // report, because the refresh is correct and only its timing is hostile.
  const { connected } = useBrandSyncLive({
    only: ["shopify"],
    enabled: shopify.store !== null,
  })

  return (
    <AppLayout>
      <Head title={t("settings.brand.integrations.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-integrations-settings" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.integrations.title")}
            description={t("settings.brand.integrations.description")}
          />

          <TiktokIntegrationCard
            connection={tiktok.connection}
            failure_reason={tiktok.failure_reason}
            canManage={can_manage}
          />

          {/* OKS-930 — second card, same `space-y-6` stack. Fully
              independent of the TikTok card; they share only
              `can_manage`. */}
          <ShopifyIntegrationCard
            store={shopify.store}
            submitted_domain={shopify.submitted_domain}
            submitted_currency={shopify.submitted_currency}
            preview={shopify.preview}
            preview_state={shopify.preview_state}
            preview_domain={shopify.preview_domain}
            live={connected}
            canManage={can_manage}
          />

          {!can_manage ? (
            <p
              data-testid="brand-integrations-readonly-hint"
              role="status"
              className="text-muted-foreground text-sm"
            >
              {t("settings.brand.integrations.read_only_hint")}
            </p>
          ) : null}
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
